import { CATALOG_PATH } from '../../Catalog.constants';
import type { CatalogEmbedConfig } from './CatalogEmbed.types';

const CATALOG_CATEGORY_PREFIX = `${CATALOG_PATH}/catalog/`;
const CATALOG_ITEM_PREFIX = `${CATALOG_PATH}/item/`;
const ITEM_SEGMENT = 'item/';

const applyAlias = (config: CatalogEmbedConfig, browserSlug: string) => config.slugAliases[browserSlug] ?? browserSlug;

const reverseAlias = (config: CatalogEmbedConfig, catalogSlug: string) => {
  const browserSlug = Object.keys(config.slugAliases).find(key => config.slugAliases[key] === catalogSlug);
  return browserSlug ?? catalogSlug;
};

/**
 * Map a public catalog path (e.g. `/shop/catalog/charity`) to the embed-relative path
 * (e.g. `/intl/add-gifts/charity`). Returns `null` when the path is not an embeddable
 * shop path, so the caller can fall back to default behavior.
 */
export const publicPathToEmbedRelative = (config: CatalogEmbedConfig, publicPath: string): string | null => {
  const base = `/${config.basePath}`;
  if (publicPath === CATALOG_PATH || publicPath === `${CATALOG_PATH}/`) {
    return base;
  }
  if (publicPath.startsWith(CATALOG_ITEM_PREFIX)) {
    return `${base}/${ITEM_SEGMENT}${publicPath.slice(CATALOG_ITEM_PREFIX.length)}`;
  }
  if (publicPath.startsWith(CATALOG_CATEGORY_PREFIX)) {
    const catalogSlug = publicPath.slice(CATALOG_CATEGORY_PREFIX.length);
    return `${base}/${reverseAlias(config, catalogSlug)}`;
  }
  return null;
};

/**
 * Map an embed-relative path (e.g. `/intl/add-gifts/charity`) back to the canonical public
 * catalog path (e.g. `/shop/catalog/charity`). Returns `null` when the path is outside the
 * embed base.
 */
export const embedRelativeToPublicPath = (config: CatalogEmbedConfig, relativePath: string): string | null => {
  const base = `/${config.basePath}`;
  if (relativePath === base || relativePath === `${base}/`) {
    return CATALOG_PATH;
  }
  if (!relativePath.startsWith(`${base}/`)) {
    return null;
  }
  const rest = relativePath.slice(base.length + 1);
  if (rest.startsWith(ITEM_SEGMENT)) {
    return `${CATALOG_ITEM_PREFIX}${rest.slice(ITEM_SEGMENT.length)}`;
  }
  const browserSlug = rest.split('/')[0].split('?')[0];
  return `${CATALOG_CATEGORY_PREFIX}${applyAlias(config, browserSlug)}`;
};

/**
 * Whether a category (identified by its own public path plus its lineage's public paths) sits
 * within one of the embed's allowed trees.
 */
export const isPublicPathWithinEmbedScope = (config: CatalogEmbedConfig, publicPaths: ReadonlyArray<Maybe<string>>): boolean => {
  return publicPaths.some(path => !!path && config.allowedRootPublicPaths.includes(path));
};
